import React, { useState, useEffect } from "react";
import Toast from "../../components/Toast";
import { userAPI } from "../../services/api";
import repaymentDatesCache from "../../Utils/repaymentDatesCache";
import Header from "../../components/Header";
import FlowFooter from "../../components/FlowFooter";

// Add modern animation styles
const modernStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
  
  @keyframes slideIn {
    0% {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  .animate-slideIn {
    animation: slideIn 0.5s ease-out;
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out;
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes bounce-subtle {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
    }
  }
  .animate-bounce-subtle {
    animation: bounce-subtle 0.6s ease-in-out;
  }

  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 20px rgba(255, 107, 53, 0.25);
    }
    50% {
      box-shadow: 0 0 30px rgba(255, 107, 53, 0.4);
    }
  }
  .animate-glow {
    animation: glow 2s ease-in-out infinite;
  }

  .hover-float:hover {
    transform: translateY(-4px);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .hover-scale:hover {
    transform: scale(1.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .glass-effect {
    backdrop-filter: blur(12px);
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(226, 232, 240, 0.8);
  }
`;

// Utility function to format amount in Indian currency format
const formatIndianCurrency = (amount) => {
  if (!amount) return "₹0";

  // Convert to number if string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Format with Indian currency format
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount);
};

export default function RepaymentCalender({ onBack, loanAmount, onRequestLoan }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isDateLocked, setIsDateLocked] = useState(false);
  const [lockedDate, setLockedDate] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [datesLoading, setDatesLoading] = useState(true);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "error",
  });

  // Eligible dates from API
  const [eligibleDates, setEligibleDates] = useState([]);
  const [eligibleMonths, setEligibleMonths] = useState([]);

  // Clear cache if loan amount changes significantly (optional feature)
  useEffect(() => {
    if (loanAmount) {
      const cachedLoanAmount = repaymentDatesCache.getLoanAmount();
      if (cachedLoanAmount && Math.abs(loanAmount - cachedLoanAmount) > 10000) {
        // If loan amount changed by more than 10k, clear cache for fresh data
        console.log("Loan amount changed significantly, clearing cache");
        repaymentDatesCache.clear();
      }
    }
  }, [loanAmount]);

  // Fetch eligible repayment dates from API
  useEffect(() => {
    const fetchEligibleDates = async () => {
      try {
        setDatesLoading(true);

        // Check if we have cached data first
        const cachedData = repaymentDatesCache.get();
        let repaymentDatesData;

        if (cachedData) {
          console.log("Using cached repayment dates data (cache age:", repaymentDatesCache.getAge(), "seconds)");
          repaymentDatesData = cachedData;
        } else {
          console.log("Fetching repayment dates from API");
          repaymentDatesData = await userAPI.getRepaymentDates(loanAmount, false);
          // Cache the response with loan amount
          repaymentDatesCache.set(repaymentDatesData, loanAmount);
          console.log("Repayment dates cached successfully");
        }

        // Convert date strings to Date objects and extract tenure and repayment amount
        const dates = repaymentDatesData.map((item) => ({
          date: new Date(item.repaymentDate),
          tenure: item.tenure,
          repaymentAmount: item.repaymentAmount
        }));
        setEligibleDates(dates);

        // Extract unique months from eligible dates
        const months = [
          ...new Set(
            dates.map((item) => `${item.date.getFullYear()}-${item.date.getMonth()}`)
          ),
        ].map((monthStr) => {
          const [year, month] = monthStr.split("-");
          return new Date(parseInt(year), parseInt(month), 1);
        });

        setEligibleMonths(months);

        // Set current month to first eligible month if available
        if (months.length > 0) {
          setCurrentMonth(months[0]);
        }

        // Set first eligible date as selected if available
        if (dates.length > 0 && !selectedDate) {
          setSelectedDate(dates[0]);
        }
      } catch (error) {
        console.error("Error fetching eligible dates:", error);
        // Clear cache on error to ensure fresh data on retry
        repaymentDatesCache.clear();
        showToast("Failed to fetch eligible dates", "error");
      } finally {
        setDatesLoading(false);
      }
    };

    fetchEligibleDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simulate page loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 2000); // Simulate 2 seconds of loading

    return () => clearTimeout(timer);
  }, []);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };


  // Generate calendar data for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= lastDay || currentDate.getDay() !== 0) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };



  // Navigate to previous month
  const goToPreviousMonth = () => {
    const currentIndex = eligibleMonths.findIndex(
      (month) =>
        month.getMonth() === currentMonth.getMonth() &&
        month.getFullYear() === currentMonth.getFullYear()
    );

    if (currentIndex > 0) {
      setCurrentMonth(eligibleMonths[currentIndex - 1]);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const currentIndex = eligibleMonths.findIndex(
      (month) =>
        month.getMonth() === currentMonth.getMonth() &&
        month.getFullYear() === currentMonth.getFullYear()
    );

    if (currentIndex < eligibleMonths.length - 1) {
      setCurrentMonth(eligibleMonths[currentIndex + 1]);
    }
  };

  // Check if current month is eligible
  const isCurrentMonthEligible = () => {
    return eligibleMonths.some(
      (month) =>
        month.getMonth() === currentMonth.getMonth() &&
        month.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    const selectedItem = eligibleDates.find(
      (item) =>
        item.date.getDate() === date.getDate() &&
        item.date.getMonth() === date.getMonth() &&
        item.date.getFullYear() === date.getFullYear()
    );

    if (selectedItem) {
      // If user selects a different date after locking, reset the locked state
      if (isDateLocked && lockedDate) {
        const isSameDate =
          selectedItem.date.getDate() === lockedDate.date.getDate() &&
          selectedItem.date.getMonth() === lockedDate.date.getMonth() &&
          selectedItem.date.getFullYear() === lockedDate.date.getFullYear();

        if (!isSameDate) {
          setIsDateLocked(false);
          setLockedDate(null);
          console.log("Date changed after locking, resetting lock state");
        }
      }

      setSelectedDate(selectedItem);
    }
  };

  // Handle locking the selected date
  const handleLockDate = async () => {
    if (!selectedDate) {
      showToast("Please select a date first", "warning");
      return;
    }

    try {
      setIsLoading(true);
      // Mock API call - replace with actual API
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsDateLocked(true);
      setLockedDate(selectedDate);
      console.log("Date locked:", selectedDate.date);
      showToast("Date locked successfully!", "success");
      // Navigate to next step or show success
    } catch {
      showToast("Failed to lock date", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return (
      date.getMonth() === currentMonth.getMonth() &&
      date.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Check if date is selected
  const isSelected = (date) => {
    return (
      selectedDate &&
      date.getDate() === selectedDate.date.getDate() &&
      date.getMonth() === selectedDate.date.getMonth() &&
      date.getFullYear() === selectedDate.date.getFullYear()
    );
  };

  // Check if date is eligible for selection (salary date)
  const isEligibleDate = (date) => {
    return eligibleDates.some(
      (item) =>
        item.date.getDate() === date.getDate() &&
        item.date.getMonth() === date.getMonth() &&
        item.date.getFullYear() === date.getFullYear()
    );
  };

  // Get repayment amount for a specific date
  const getRepaymentAmount = (date) => {
    const dateItem = eligibleDates.find(
      (item) =>
        item.date.getDate() === date.getDate() &&
        item.date.getMonth() === date.getMonth() &&
        item.date.getFullYear() === date.getFullYear()
    );
    return dateItem?.repaymentAmount || 0;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];

  // Handle proceed action after date is locked
  const handleProceed = async () => {
    console.log("handleProceed called");
    console.log("selectedDate:", selectedDate);
    console.log("isDateLocked:", isDateLocked);
    console.log("onRequestLoan:", onRequestLoan);

    if (!selectedDate || !isDateLocked) {
      showToast("Please lock a date first", "warning");
      return;
    }

    try {
      setIsLoading(true);

      // Format date as YYYY-MM-DD without timezone conversion
      const year = selectedDate.date.getFullYear();
      const month = String(selectedDate.date.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;
      console.log("repayment date---->", formattedDate);
      console.log("tenure---->", selectedDate.tenure);
      console.log("repayment amount---->", selectedDate.repaymentAmount);

      // Store the selected date, tenure, and repayment amount in localStorage
      localStorage.setItem("selectedRepaymentDate", formattedDate);
      localStorage.setItem("selectedTenure", selectedDate.tenure);
      localStorage.setItem("selectedRepaymentAmount", selectedDate.repaymentAmount);
      console.log("Data stored in localStorage");

      // Call the handleRequestLoan function from parent component
      if (onRequestLoan) {
        console.log("Calling onRequestLoan function");
        await onRequestLoan();
        console.log("onRequestLoan completed");
      } else {
        console.log("No onRequestLoan function provided");
        showToast("Proceeding to next step...", "success");
        // Navigate back to the loan amount page
        window.history.back();
      }
    } catch (error) {
      console.error("Proceed error:", error);
      showToast("Failed to proceed", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const LoadingSkeleton = () => (
    <div className="w-full space-y-3 sm:space-y-4 animate-fadeIn">
      <div className="w-full rounded-2xl overflow-hidden bg-[#F8FAFC] border border-[#E2E8F0]">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="w-10 h-10 rounded-full animate-pulse bg-[#E2E8F0]"></div>
          <div className="w-32 h-6 rounded animate-pulse bg-[#E2E8F0]"></div>
          <div className="w-10 h-10 rounded-full animate-pulse bg-[#E2E8F0]"></div>
        </div>
        <div className="grid grid-cols-7 gap-2 p-4">
          {Array.from({ length: 21 }).map((_, i) => (
            <div key={i} className="min-h-[48px] rounded-xl animate-pulse bg-[#E2E8F0]"></div>
          ))}
        </div>
      </div>
      <div className="w-full flex gap-4 justify-center">
        <div className="w-24 h-12 rounded-2xl animate-pulse bg-[#E2E8F0]"></div>
        <div className="w-40 h-12 rounded-2xl animate-pulse bg-[#E2E8F0]"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative overflow-hidden flex flex-col">
      <style>{modernStyles}</style>
      <Header />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-6 pb-10 px-4 sm:px-6 lg:px-10">
        <div className="max-w-xl w-full mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A8A]">
              Select Your <span className="text-[#FF6B35]">Repayment</span> Date
            </h1>
            <p className="text-[#64748B] mt-2">Choose when you want to repay your loan</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-6 sm:p-8 shadow-2xl shadow-[#FF6B35]/10">

              {/* Main Container */}
              <div className="w-full space-y-3 sm:space-y-4">
                {/* Show loading skeleton or actual content */}
                {pageLoading || datesLoading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    {isCurrentMonthEligible() ? (
                      <div className="w-full rounded-xl sm:rounded-2xl overflow-hidden animate-slideIn shadow-xl bg-white border border-[#E2E8F0]">
                        <div className="mt-1 rounded-t-xl sm:rounded-t-2xl bg-[#F8FAFC] border-b border-[#E2E8F0]">
                          <div className="flex items-center justify-between px-2 sm:px-3 md:px-4 py-0 sm:py-1">
                            <button
                              onClick={goToPreviousMonth}
                              disabled={
                                eligibleMonths.findIndex(
                                  (month) =>
                                    month.getMonth() ===
                                    currentMonth.getMonth() &&
                                    month.getFullYear() ===
                                    currentMonth.getFullYear()
                                ) <= 0
                              }
                              className="p-1 sm:p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-[#1E293B] transition-all duration-300 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FF6B35]/20 hover:border-[#FF6B35]/40 cursor-pointer transform hover:scale-110"
                            >
                              <svg
                                className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                            <h4 className="text-sm md:text-base font-bold text-[#1E293B] text-center flex-1 tracking-wide cursor-default">
                              {monthNames[currentMonth.getMonth()]}{" "}
                              {currentMonth.getFullYear()}
                            </h4>
                            <button
                              onClick={goToNextMonth}
                              disabled={
                                eligibleMonths.findIndex(
                                  (month) =>
                                    month.getMonth() ===
                                    currentMonth.getMonth() &&
                                    month.getFullYear() ===
                                    currentMonth.getFullYear()
                                ) >=
                                eligibleMonths.length - 1
                              }
                              className="p-1 sm:p-2 rounded-lg bg-[#F8FAFC] text-[#1E293B] border border-[#E2E8F0] text-[#1E293B] transition-all duration-300 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FF6B35]/20 hover:border-[#FF6B35]/40 cursor-pointer transform hover:scale-110"
                            >
                              <svg
                                className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>

                          {/* Modern Days of Week Header */}
                          <div className="grid grid-cols-7 gap-1 sm:gap-2 px-2 py-0 sm:p-1">
                            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
                              (day) => (
                                <div key={day} className="text-center">
                                  <span className="text-[8px] sm:text-xs font-bold text-[#64748B] tracking-wider cursor-default">
                                    {day}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-7 gap-1 sm:gap-2 bg-[#F8FAFC] p-2 sm:p-3 rounded-b-xl sm:rounded-b-2xl">
                          {calendarDays.map((date, index) =>
                            isCurrentMonth(date) && isEligibleDate(date) ? (
                              <button
                                key={index}
                                onClick={() => handleDateSelect(date)}
                                className={`
                                  relative p-1 sm:p-2 rounded-xl text-[8px] sm:text-xs font-semibold transition-all duration-300 flex flex-col items-center justify-center min-h-[38px] sm:min-h-[52px] group shadow-md hover:shadow-lg cursor-pointer border-2
                                  ${isSelected(date)
                                    ? "scale-105 shadow-lg border-[#FF6B35] ring-2 ring-[#FF6B35]/40 bg-[#FF6B35]/10"
                                    : "bg-white hover:bg-[#FF6B35]/5 hover:scale-105 border-[#E2E8F0] hover:border-[#FF6B35]/50"
                                  }
                                `}
                                style={isSelected(date) ? { backgroundColor: 'rgba(255,107,53,0.15)', borderColor: '#FF6B35' } : {}}
                              >
                                <span
                                  className={`font-bold transition-all duration-300 text-xs sm:text-sm mb-1 ${isSelected(date)
                                    ? "text-[#1E293B]"
                                    : "group-hover:text-[#00CED1]"
                                    }`}
                                  style={!isSelected(date) ? { color: '#00CED1' } : {}}
                                >
                                  {date.getDate()}
                                </span>
                                <span
                                  className={`text-[6px] sm:text-[8px] font-bold leading-tight text-center transition-all duration-300 ${isSelected(date)
                                    ? "text-[#64748B]"
                                    : "text-[#94A3B8] group-hover:text-[#64748B]"
                                    }`}
                                >
                                  {formatIndianCurrency(getRepaymentAmount(date))}
                                </span>
                              </button>
                            ) : isCurrentMonth(date) && !isEligibleDate(date) ? (
                              <div
                                key={index}
                                className="min-h-[38px] sm:min-h-[52px] flex items-center justify-center bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]"
                              >
                                <span className="text-[#94A3B8] text-xs sm:text-sm font-medium">
                                  {date.getDate()}
                                </span>
                              </div>
                            ) : (
                              <div key={index} className="min-h-[38px] sm:min-h-[52px]"></div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full rounded-2xl overflow-hidden p-6 text-center animate-slideIn bg-white/5 border border-white/10">
                        <p className="text-white text-sm">
                          No eligible repayment dates available for this period.
                        </p>
                      </div>
                    )}

                    {selectedDate && (
                      <div className="w-full bg-white/5 border border-white/10 rounded-xl p-3 animate-slideIn md:hidden">
                        <div className="flex justify-between items-center">
                          <div className="text-left">
                            <p className="text-[10px] text-gray-400 mb-1">Estimate payback</p>
                            <p className="text-xs font-bold text-[#00CED1]">
                              {formatIndianCurrency(selectedDate.repaymentAmount)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-[#94A3B8] mb-1">Tenure</p>
                            <p className="text-xs font-bold text-[#FF6B35]">
                              {selectedDate.tenure} days
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Modern Action Buttons Section */}
                    {isCurrentMonthEligible() && (
                      <div
                        className="w-full flex gap-3 sm:gap-4 md:gap-6 justify-center animate-slideIn px-1"
                        style={{ animationDelay: "0.2s" }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            // Clear any stored data
                            localStorage.removeItem("selectedRepaymentDate");
                            localStorage.removeItem("selectedTenure");
                            localStorage.removeItem("selectedRepaymentAmount");
                            // Call the onBack callback to turn off the component
                            if (onBack) {
                              onBack();
                            }
                          }}
                          className="py-2 sm:py-3 px-4 sm:px-6 rounded-2xl text-sm font-semibold bg-white/10 text-[#1E293B] border border-white/10 hover:bg-[#FF69B4]/30 hover:border-[#FF69B4]/50 transition-all cursor-pointer"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          disabled={!selectedDate || isLoading}
                          className="py-2 sm:py-3 px-4 sm:px-8 rounded-2xl text-sm font-semibold bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex-1 sm:flex-none shadow-md"
                          onClick={
                            isDateLocked && lockedDate && selectedDate &&
                              lockedDate.date.getTime() === selectedDate.date.getTime()
                              ? handleProceed
                              : handleLockDate
                          }
                        >
                          {isLoading
                            ? "PROCESSING..."
                            : isDateLocked && lockedDate && selectedDate &&
                              lockedDate.date.getTime() === selectedDate.date.getTime()
                              ? "Request Loan"
                              : "Lock this date"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}
